import { debounce } from 'lodash';
import { useRef, useState, useEffect } from 'react';
import Button from '../components/ui/Button';
import MyAudioRecorder, { MyAudioRecorderHandle } from '../components/ui/MyAudioRecorder';
import { useApiReload } from '../context/apiRelaod';
import Card, { CardBody, CardHeader, CardTitle } from '../components/ui/Card';
import { useAudioRecording, RecordingStatus } from '../context/AudioRecordingContext';
import { toast } from 'react-toastify';

const AudioRecorderComponent = ({ selectedNote }: { selectedNote?: string }) => {
	const { setIsOnNewNoteScreen, patientID, caseInfo } = useApiReload();
	const { recordings, activeRecordingId, formatTime, getRecordingStatus } = useAudioRecording();

	const [recordingAnimation, setRecordingAnimation] = useState<boolean>(false);
	const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
	const [processingStop, setProcessingStop] = useState<boolean>(false);
	const [isProcessing, setIsProcessing] = useState<boolean>(false);

	const audioRecorderRef = useRef<MyAudioRecorderHandle>(null);

	// Get patient/case specific info
	const patientId = patientID?.id || 'Anonymous';
	const soapId = caseInfo?.id || '-1';
	const recordingId = `${patientId}:${soapId}`;

	// Get recording status for this patient
	const patientRecording = getRecordingStatus(patientId, soapId);

	// Use patient-specific status
	const isRecording = patientRecording.status === RecordingStatus.RECORDING;
	const isPaused = patientRecording.status === RecordingStatus.PAUSED;
	const isStopped =
		patientRecording.status === RecordingStatus.COMPLETED ||
		patientRecording.status === RecordingStatus.IDLE;
	const currentRecordingTime = patientRecording.time;
	const isActive = patientRecording.isActive || currentRecordingTime > 0;

	// Animation effect for recording indicator
	useEffect(() => {
		if (isRecording) {
			const interval = setInterval(() => {
				setRecordingAnimation((prev) => !prev);
			}, 800);
			return () => clearInterval(interval);
		} else {
			setRecordingAnimation(false);
		}
	}, [isRecording]);

	const handleStartRecording = async () => {
		try {
			// Prevent multiple clicks
			if (isProcessing) return;
			setIsProcessing(true);

			// Check if we're already at the limit of 3 recordings
			if (Object.keys(recordings).length >= 3 && !recordings[recordingId]) {
				setIsOnNewNoteScreen(false);
				toast.error('You can only record 3 audios at a time');
				setIsProcessing(false);
				return;
			}

			setIsOnNewNoteScreen(true);
			await audioRecorderRef.current?.startRecording();
			setIsProcessing(false);
		} catch (error) {
			setIsProcessing(false);
			console.error('Error starting recording:', error);
			toast.error('Failed to start recording. Please try again.');
		}
	};

	const handleStopRecording = async (): Promise<void> => {
		try {
			// Prevent multiple clicks
			if (processingStop || isProcessing) return;

			setProcessingStop(true);
			setIsProcessing(true);
			setIsOnNewNoteScreen(false);

			// Show a loading toast
			const loadingToastId = toast.loading('Processing recording...');

			// This will trigger the upload to the server directly
			const success = await audioRecorderRef.current?.stopRecording();

			// Update toast based on result
			if (success) {
				toast.update(loadingToastId, {
					render: 'Recording is being processed. SOAP note will be generated shortly.',
					type: 'success',
					isLoading: false,
					autoClose: 5000,
				});
			} else {
				toast.update(loadingToastId, {
					render: "Could not process recording. Make sure it's at least 3 minutes long.",
					type: 'error',
					isLoading: false,
					autoClose: 5000,
				});
				setProcessingStop(false);
				setIsProcessing(false);
			}
		} catch (error) {
			console.error('Error stopping recording:', error);
			toast.error('Failed to stop recording. Please try again.');
			setProcessingStop(false);
			setIsProcessing(false);
		}
	};

	const handlePauseResume = async () => {
		try {
			// Prevent multiple clicks
			if (isProcessing) return;
			setIsProcessing(true);

			// Toggle isOnNewNoteScreen based on current status
			if (patientRecording.status === RecordingStatus.PAUSED) {
				setIsOnNewNoteScreen(true);
			} else if (patientRecording.status === RecordingStatus.RECORDING) {
				setIsOnNewNoteScreen(false);
			}

			await audioRecorderRef.current?.pauseResume();
			setIsProcessing(false);
		} catch (error) {
			setIsProcessing(false);
			console.error('Error toggling pause/resume:', error);
			toast.error('Failed to pause/resume recording. Please try again.');
		}
	};

	const handleDeleteRecording = async (): Promise<void> => {
		try {
			// Prevent multiple clicks
			if (isProcessing) return;
			setIsProcessing(true);

			setIsOnNewNoteScreen(false);
			setShowConfirmDelete(false);

			// Call the deleteRecording method
			if (audioRecorderRef.current?.deleteRecording) {
				await audioRecorderRef.current.deleteRecording();
				toast.success('Recording discarded successfully');
			}

			setIsProcessing(false);
		} catch (error) {
			setIsProcessing(false);
			console.error('Error deleting recording:', error);
			toast.error('Failed to delete recording. Please try again.');
		}
	};

	const debouncedPaused = debounce(handlePauseResume, 300);
	const debouncedStart = debounce(handleStartRecording, 300);

	return (
		<Card className='mx-auto mb-16 mt-7 max-w-md shadow-lg'>
			<CardHeader>
				<CardTitle className='w-full justify-center text-center text-blue-950'>
					Audio Recording
					{Object.keys(recordings).length > 0 && (
						<span className='primary-text ml-2 text-sm font-normal'>
							({Object.keys(recordings).length}/3 recordings in progress)
						</span>
					)}
				</CardTitle>
			</CardHeader>
			<CardBody className='flex flex-col items-center gap-6 pb-8'>
				{/* Recording status indicator */}
				<div className='relative flex items-center justify-center'>
					<div
						className={`absolute -left-8 h-4 w-4 rounded-full ${isRecording ? 'bg-red-500' : 'bg-gray-300'} ${recordingAnimation ? 'opacity-100' : 'opacity-50'} transition-opacity`}></div>
					<h3 className='text-4xl font-bold text-blue-950'>
						{formatTime(currentRecordingTime)}
					</h3>
				</div>

				{/* Recording status text */}
				<div className='text-center text-gray-600'>
					{processingStop
						? 'Processing recording...'
						: isProcessing
							? 'Processing...'
							: isRecording
								? 'Recording in progress...'
								: isPaused
									? 'Recording paused'
									: isActive
										? 'Recording stopped'
										: 'Ready to record'}
				</div>

				{/* Control buttons */}
				<div className='flex flex-wrap justify-center gap-4'>
					{!isActive ? (
						<Button
							variant='solid'
							colorIntensity='500'
							icon='mic'
							className='primary-bg min-w-32 text-base font-medium'
							onClick={debouncedStart}
							isDisable={isProcessing}>
							Start Recording
						</Button>
					) : null}

					{isActive && !showConfirmDelete && !processingStop ? (
						<>
							<Button
								variant='outline'
								colorIntensity='500'
								icon={isPaused ? 'play' : 'pause'}
								className='primary-bg min-w-32 font-medium text-white disabled:opacity-50'
								onClick={debouncedPaused}
								isDisable={isProcessing}>
								{isPaused ? 'Resume' : 'Pause'}
							</Button>

							<Button
								variant='solid'
								color='red'
								colorIntensity='500'
								icon='stop'
								className='min-w-32 text-base font-medium disabled:opacity-50'
								isDisable={isStopped || isProcessing}
								onClick={() => handleStopRecording()}>
								Stop Recording
							</Button>

							<Button
								variant='outline'
								color='red'
								colorIntensity='500'
								icon='trash'
								className='min-w-32 text-base font-medium'
								isDisable={isProcessing}
								onClick={() => setShowConfirmDelete(true)}>
								Discard
							</Button>
						</>
					) : null}

					{/* Confirmation Dialog */}
					{showConfirmDelete && (
						<div className='mt-2 w-full'>
							<div className='rounded-md bg-red-50 p-4 text-center'>
								<p className='mb-4 text-sm text-red-800'>
									Are you sure you want to discard this recording?
								</p>
								<div className='flex justify-center gap-3'>
									<Button
										variant='outline'
										colorIntensity='500'
										size='sm'
										className='primary-bg text-white'
										isDisable={isProcessing}
										onClick={() => setShowConfirmDelete(false)}>
										Cancel
									</Button>
									<Button
										variant='solid'
										color='red'
										colorIntensity='500'
										size='sm'
										className='text-sm'
										isDisable={isProcessing}
										onClick={handleDeleteRecording}>
										Yes, Discard
									</Button>
								</div>
							</div>
						</div>
					)}

					{/* Processing Indicator */}
					{(processingStop || isProcessing) && (
						<div className='w-full text-center'>
							<div
								className='inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]'
								role='status'>
								<span className='!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]'>
									Processing...
								</span>
							</div>
							<p className='mt-2 text-sm text-gray-600'>
								{processingStop
									? 'Processing your recording to generate SOAP note...'
									: 'Processing...'}
							</p>
						</div>
					)}
				</div>
			</CardBody>

			{/* MyAudioRecorder with updated props */}
			<div className='hidden'>
				<MyAudioRecorder selectedNote={selectedNote} ref={audioRecorderRef} />
			</div>
		</Card>
	);
};

export default AudioRecorderComponent;
